import { useEffect, useState } from "react";
import { Select } from "risa-oasis-ui_v2";
import { FirestoreService } from "../../../../../api/firebase/firestoreService";
import { FirestoreCollectionReference } from "../../../../../api/firebase/references";
import {
  FollowUpTypeOption,
  FollowUpTypeResponse,
} from "../../../../../data-model/followUpTypeModel";
import DashedLines from "../../../../../svg/dashedLines";
import { getOrgIdForFetchExternalWorklist } from "../../../../../utils/organizationHelper";

export interface LogEvenetsProps {
  templates: string[];
  setComments: (comments: string) => void;
  setSelectedFollowUpType: (followUpType: {
    id: string;
    label: string;
  }) => void;
}

export const LogEvenets = (props: LogEvenetsProps) => {
  const [selectedTemplates, setSelectedTemplates] = useState<number[]>([]);
  const [followUpTypeOptions, setFollowUpTypeOptions] = useState<
    FollowUpTypeOption[]
  >([]);
  const [manualComment, setManualComment] = useState<string>("");

  const getFollowUpType = async () => {
    try {
      const organization = getOrgIdForFetchExternalWorklist();
      const response =
        await FirestoreService.getAllDocuments<FollowUpTypeResponse>(
          FirestoreCollectionReference.followUpType(organization),
        );

      const followUpTypeData = response.find(
        (item) => item.id === "follow_up_type",
      )?.data;

      const followUpTypeOptions: FollowUpTypeOption[] =
        (followUpTypeData as unknown as FollowUpTypeOption[]) || [];

      setFollowUpTypeOptions(followUpTypeOptions);
    } catch (error) {
      console.error("Error fetching follow-up types:", error);
      setFollowUpTypeOptions([]);
    }
  };

  useEffect(() => {
    getFollowUpType();
  }, []);

  return (
    <div className="status-log--log-event flex flex-col gap-4">
      <Select
        id="status-log-select"
        onOptionChange={(selectedValue) => {
          const selectedOption = followUpTypeOptions.find(
            (option) => option.value === selectedValue.value,
          );
          if (selectedOption) {
            props.setSelectedFollowUpType({
              id: selectedOption.value,
              label: selectedOption.label,
            });
          }
        }}
        label="Follow Up Type"
        placeholder="Select the follow up type"
        options={followUpTypeOptions}
      />

      <div className="status-log--body--follow-up-type flex w-full flex-col gap-3">
        <div className="status-log--body--follow-up-type--label w-full text-h11 font-bold text-primaryGray-1">
          Select a Template
        </div>
        {props.templates.map((template, index) => (
          <div
            key={index}
            className={`status-log--body--follow-up-type--templates flex w-full cursor-pointer flex-col gap-2 rounded rounded-s bg-primaryGray-16 p-3 transition-all duration-200 ${
              selectedTemplates.includes(index)
                ? "border-1 border-primaryGray-1 text-primaryGray-1"
                : "border-1 border-transparent hover:border-gray-300"
            }`}
            onClick={() => {
              if (selectedTemplates.includes(index)) {
                // Deselect template
                const newSelectedTemplates = selectedTemplates.filter(
                  (i) => i !== index,
                );
                setSelectedTemplates(newSelectedTemplates);

                // Update comments based on remaining selected templates
                const remainingComments = newSelectedTemplates
                  .map((i) => props.templates[i])
                  .join("; ");
                props.setComments(remainingComments);
              } else {
                // Select template
                const newSelectedTemplates = [...selectedTemplates, index];
                setSelectedTemplates(newSelectedTemplates);

                // Update comments with all selected templates
                const allComments = newSelectedTemplates
                  .map((i) => props.templates[i])
                  .join("; ");
                props.setComments(allComments);

                // Clear manual comment when template is selected
                setManualComment("");
              }
            }}
          >
            <div className="flex w-full text-wrap text-primaryGray-3">
              {template}
            </div>
          </div>
        ))}
      </div>

      {selectedTemplates.length === 0 && (
        <div className="status-log--body--follow-up-type--templates flex w-full flex-row items-center gap-2">
          <DashedLines />
          <div className="text-h11 font-normal text-primaryGray-1">Or</div>
          <DashedLines />
        </div>
      )}

      {selectedTemplates.length === 0 && (
        <div className="comments-section w-full">
          <div className="comments-section header mb-2 text-xs font-normal text-primaryGray-2">
            Enter Manually
          </div>
          <div className="comments-section comments-textarea">
            <textarea
              className="h-42 w-full rounded border border-gray-300 p-2 text-tiny font-normal"
              placeholder="Enter your general comments here"
              value={manualComment}
              onChange={(e) => {
                setManualComment(e.target.value);
                props.setComments(e.target.value);
                // Clear template selections when manual comment is entered
                if (e.target.value.trim() && selectedTemplates.length > 0) {
                  setSelectedTemplates([]);
                }
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};
